import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { ContractModel, MemberContractModel } from "src/api/contract/contract-types";
import { ConfirmModal } from "src/components";
import { BaseRadio } from "src/components/layout/BaseRadio";
import { Modal } from "src/pages/visitor/visitor-types";
import { useMemberInfoState } from "src/recoil/member/hook";
import { YmdFormat, onlyNumber } from "src/utils/common-util";
import CashBillForm from "./switchInvoice/CashBillForm";
import TaxInvoiceInfo from "./switchInvoice/TaxInvoiceInfo";

// 결제 수단 선택 컴포넌트의 props 타입 정의
type Props = {
  contracts: MemberContractModel[] | null;
  contract?: ContractModel; // 현재 계약 정보 (pgcodeOption 접근용)
  currentPgCode: string; // 현재 선택된 결제 수단 코드
  recentBill: any; // 최근 결제 정보
  newPgCode: Dispatch<SetStateAction<string>>; // 결제 수단 변경 핸들러
  documentProofRequest: any; // 증빙서류 요청 정보
  setDocumentProofRequest: any; // 증빙서류 요청 정보 설정 핸들러
  proofError: any; // 증빙서류 에러 정보
  setProofError: any; // 증빙서류 에러 정보 설정 핸들러
  isFreeDisabled?: boolean; // 무료 상품인경우 버튼 비활성화
  identityNumError?: string; // 신분증 번호 에러 정보
  setIdentityNumError?: (error: string) => void; // 신분증 번호 에러 정보 설정 핸들러
  isNotFree?: boolean; // 온라인 결제 불가 상품이면서 유료인 경우
};

// 결제 수단 선택 컴포넌트
const ServiceTypePaymentMethodSelector = ({
  contracts,
  contract,
  recentBill,
  currentPgCode,
  newPgCode,
  documentProofRequest,
  setDocumentProofRequest,
  proofError,
  setProofError,
  isFreeDisabled,
  identityNumError: propIdentityNumError,
  setIdentityNumError: propSetIdentityNumError,
  isNotFree,
}: Props) => {
  const { contractId } = useParams();
  const { t } = useTranslation("contract");
  const { member } = useMemberInfoState();
  const partnerId = contracts?.find((contract) => contract?.contractId === contractId)?.partnerId;

  // pgcodeOption에서 활성화 여부 확인
  const pgcodeOptions = contract?.pgcodeOption?.items || [];
  const isCreditCardEnabled = pgcodeOptions.find((opt: any) => opt.pgcode === "creditcard")?.isEnabled ?? false; // 기본값 false
  const isVirtualAccountEnabled = pgcodeOptions.find((opt: any) => opt.pgcode === "virtualaccount")?.isEnabled ?? false; // 기본값 false

  // 기본 결제 수단 결정: 둘 다 true면 신용카드, isCreditCardEnabled가 false면 가상계좌
  const getDefaultPgCode = () => {
    if (isCreditCardEnabled) {
      return "creditcard";
    }
    if (isVirtualAccountEnabled) {
      return "virtualaccount";
    }
    return currentPgCode;
  };

  // 결제 수단 상태 관리 - 기본값을 getDefaultPgCode()로 설정
  const [pgCode, setPgCode] = useState<string>(getDefaultPgCode);
  const [invoice, setInvoice] = useState<string>("");
  const [proofOption, setProofOption] = useState<string>("noProof");
  const [localIdentityNumError, setLocalIdentityNumError] = useState<string>("");
  const identityNumError = propIdentityNumError !== undefined ? propIdentityNumError : localIdentityNumError;
  const [isAlertModalOpen, setIsAlertModalOpen] = useState<Modal>({ isOpen: false, message: "" });
  const setIdentityNumError = propSetIdentityNumError || setLocalIdentityNumError;

  // pgcodeOption 변경 시 기본 결제 수단 설정
  useEffect(() => {
    const defaultPgCode = getDefaultPgCode();
    if (defaultPgCode !== pgCode) {
      setPgCode(defaultPgCode);
      newPgCode(defaultPgCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreditCardEnabled, isVirtualAccountEnabled]);

  // 증빙서류 타입이 변경될 때 invoice 상태 업데이트
  useEffect(() => {
    if (documentProofRequest.expenseProofType !== null) {
      setInvoice(documentProofRequest.expenseProofType);
    }
  }, [documentProofRequest]);

  // identityType이 MOBILE_NUM이고 identityNum이 비어있을 때 회원정보의 phoneNumber를 기본값으로 설정
  useEffect(() => {
    if (documentProofRequest?.identityType === "MOBILE_NUM" && !documentProofRequest?.identityNum && member?.phoneNumber) {
      setDocumentProofRequest({
        ...documentProofRequest,
        identityNum: onlyNumber(member.phoneNumber),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentProofRequest?.identityType, member?.phoneNumber]);

  return (
    <section className={`base-section-layout ${isNotFree ? '' : 'top-divider'}`}>
      {isNotFree ? (
        <>
          <p className="not-free-message">{t("detail.onlinePaymentUnavailable")}</p>
        </>) : (
        <>
          <div className="base-section-layout__container">
            <div className="pb20">
              <div className="base-section-layout__title-wrap">
                <h2 className="mypage_required">{t("detail.paymentMethod")}</h2>
              </div>

              <p className="font14 text-primary3 lh-20">
                <Trans
                  t={t}
                  i18nKey="detail.completePaymentNotice"
                  values={{ date: moment(recentBill?.billDueDate).format(YmdFormat.YYYY_MM_DD_HH_MM) }}
                  components={{ red: <span className="text-red" />, br: <br /> }}
                />
              </p>

            </div>
            <div className="py20 border-top">
              {/* 결제 수단 선택 라디오 버튼 */}
              <div className="radio-button-wrap">
                <BaseRadio
                  id="creditCard"
                  name="pgCode"
                  value={"creditcard"}
                  label={t("detail.creditCard")}
                  disabled={isFreeDisabled}
                  className={!isCreditCardEnabled ? "view-disabled" : ""}
                  checked={pgCode === "creditcard"}
                  onChange={(value: boolean) => {
                    if (isFreeDisabled || !isCreditCardEnabled) {
                      setIsAlertModalOpen({ isOpen: true });
                      return;
                    }
                    setPgCode("creditcard");
                    newPgCode("creditcard");
                    if (documentProofRequest.contractId !== null) {
                      setDocumentProofRequest({
                        ...documentProofRequest,
                      });
                    }
                  }}
                />
                <BaseRadio
                  id="virtualAccount"
                  name="pgCode"
                  className={!isVirtualAccountEnabled ? "view-disabled" : ""}
                  value={"virtualaccount"}
                  label={t("detail.virtualAccount")}
                  disabled={isFreeDisabled}
                  checked={pgCode === "virtualaccount"}
                  onChange={(value: boolean) => {
                    if (isFreeDisabled || !isVirtualAccountEnabled) {
                      setIsAlertModalOpen({ isOpen: true });
                      return;
                    }
                    setPgCode("virtualaccount");
                    newPgCode("virtualaccount");
                    setDocumentProofRequest({
                      ...documentProofRequest,
                      expenseProofType: "CASHBILL",
                      identityType: null,
                    });
                  }}
                />
              </div>
              {/* 가상계좌 선택 시 지출증빙 선택  */}
              {pgCode === "virtualaccount" && (
                <div className="pb10">
                  <div className="py20 border-top mt20">
                    <div className="base-section-layout__title-wrap">
                      <h2 className="mypage_required">{t("detail.expenseDocumentation")}</h2>
                    </div>
                    <BaseRadio
                      id="noProof"
                      name="proofOption"
                      value={"noProof"}
                      label={t("detail.noDocumentation")}
                      className="mt10"
                      disabled={isFreeDisabled}
                      checked={proofOption === "noProof"}
                      onChange={(value: boolean) => {
                        setProofOption("noProof");
                        setDocumentProofRequest({
                          ...documentProofRequest,
                          isRequest: false,
                          expenseProofType: "CASHBILL",
                          identityType: "MOBILE_NUM",
                          tradeUsage: "INCOME",
                          identityNum:
                            documentProofRequest.contractId === null || Number(documentProofRequest.contractId) === 0
                              ? null
                              : documentProofRequest.identityNum,
                          mediaList: [],
                        });
                      }}
                    />
                    {proofOption === "noProof" && (
                      <p className="font14 text-primary3 pl28 pt10 lh-20">
                        <Trans t={t} i18nKey="detail.selfIssueNotice" components={{ br: <br /> }} />
                      </p>
                    )}
                  </div>
                  <BaseRadio
                    id="proof"
                    name="proofOption"
                    value={"proof"}
                    label={t("detail.requestDocumentation")}
                    disabled={isFreeDisabled}
                    checked={proofOption === "proof"}
                    onChange={(value: boolean) => {
                      setProofOption("proof");
                      setDocumentProofRequest({
                        ...documentProofRequest,
                        isRequest: true,
                        expenseProofType: "CASHBILL",
                        identityType: "MOBILE_NUM",
                        tradeUsage: "INCOME",
                        identityNum: member?.phoneNumber ? onlyNumber(member.phoneNumber) : "",
                        mediaList: [],
                      });
                      setInvoice("CASHBILL");
                    }}
                  />
                </div>
              )}
              {/* 가상계좌 선택 시 증빙서류 타입 선택 */}
              {pgCode === "virtualaccount" && proofOption === "proof" && (
                <div className="means-of-proof">
                  <div className="base-section-layout__title-wrap">
                    <h2 className="mypage_required">{t("detail.proofMethod")}</h2>
                  </div>
                  <div className="tab-radio">
                    <div className="tab-radio__wrap">
                      <input
                        id="CASHBILL"
                        type="radio"
                        name="invoice"
                        value={"CASHBILL"}
                        checked={invoice === "CASHBILL"}
                        onChange={() => {
                          setDocumentProofRequest({
                            ...documentProofRequest,
                            isRequest: true,
                            expenseProofType: "CASHBILL",
                            identityType: "MOBILE_NUM",
                            tradeUsage: "INCOME",
                            identityNum:
                              documentProofRequest.contractId === null || Number(documentProofRequest.contractId) === 0
                                ? member?.phoneNumber
                                  ? onlyNumber(member.phoneNumber)
                                  : ""
                                : documentProofRequest.identityNum,
                            mediaList: [],
                          });
                          setInvoice("CASHBILL");
                        }}
                      />
                      <label htmlFor="CASHBILL" className={`tab-radio__label ${invoice === "CASHBILL" ? "--active exist-radius" : ""}`}>
                        {t("detail.cashReceipt")}
                      </label>
                      <input
                        id="TAX_INVOICE"
                        name="invoice"
                        className="tab-radio"
                        type="radio"
                        value={"TAX_INVOICE"}
                        checked={invoice === "TAX_INVOICE"}
                        onChange={() => {
                          setInvoice("TAX_INVOICE");
                          setDocumentProofRequest({
                            ...documentProofRequest,
                            isRequest: true,
                            expenseProofType: "TAX_INVOICE",
                            identityNum: "",
                            identityType: "CORP_NUM",
                          });
                        }}
                      />
                      <label htmlFor="TAX_INVOICE" className={`tab-radio__label ${invoice === "TAX_INVOICE" ? "--active exist-radius" : ""}`}>
                        {t("detail.taxInvoice")}
                      </label>
                    </div>
                  </div>
                  <div className="base-section-layout-line flex-row flex-center-start s2-payment-info">
                    {/* 세금계산서 정보 입력 폼 */}
                    {invoice === "CASHBILL" && (
                      <CashBillForm
                        documentProofRequest={documentProofRequest}
                        setDocumentProofRequest={setDocumentProofRequest}
                        identityNumError={identityNumError}
                        setIdentityNumError={setIdentityNumError}
                        member={member}
                      />
                    )}
                    {invoice === "TAX_INVOICE" && (
                      <TaxInvoiceInfo
                        mode="edit"
                        documentProofRequest={documentProofRequest}
                        setDocumentProofRequest={setDocumentProofRequest}
                        proofError={proofError}
                        setProofError={setProofError}
                      />
                    )}
                  </div>
                </div>
              )}
              {isAlertModalOpen.isOpen && (
                <ConfirmModal
                  isOpen={true}
                  btnRightTitle={t("common:confirm")}
                  onClick={() => {
                    setIsAlertModalOpen({ isOpen: false });
                  }}
                >
                  {<p className="font18 font-weight-600">{t("detail.unsupportedPaymentMethod")}</p>}
                  {<p className="font14 mt20 font-weight-400 text-primary3">{t("detail.selectAnotherPaymentMethod")}</p>}
                </ConfirmModal>
              )}
            </div>
          </div>
        </>)}
    </section>
  );
};

export default ServiceTypePaymentMethodSelector;
